import React, { useState, forwardRef } from "react";
import PropTypes from "prop-types";
import { Input, Label } from "reactstrap";
import "../../assets/scss/custom/components/CustomInput.scss";
import eyeIcon from "../../assets/images/authentication/eye.svg";
import eyeSlashIcon from "../../assets/images/authentication/eye-slash.svg";

/**
 * CustomInput
 * Reusable text input styled to match the Omaha-style auth screens.
 * Drop-in replacement for reactstrap's <Input>, wired the same way
 * (Formik handleChange / handleBlur / values / touched / errors).
 *
 * Usage:
 * <CustomInput
 *   label="Email"
 *   name="email"
 *   type="email"
 *   placeholder="alex@omaha.com"
 *   value={validation.values.email}
 *   onChange={validation.handleChange}
 *   onBlur={validation.handleBlur}
 *   invalid={validation.touched.email && !!validation.errors.email}
 *   errorText={validation.errors.email}
 * />
 */
const CustomInput = forwardRef(
  (
    {
      label,
      name,
      id,
      type = "text",
      placeholder,
      value,
      onChange,
      onBlur,
      invalid,
      errorText,
      optional,
      className,
      showPasswordIcon,
      hidePasswordIcon,
      innerRef,
      ...rest
    },
    ref,
  ) => {
    const inputId = id || name;
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === "password";
    const inputType = isPassword && showPassword ? "text" : type;

    return (
      <div className={`custom-input-wrapper ${className || ""}`}>
        <div className={`custom-input-group ${invalid ? "is-invalid" : ""}`}>
          {label && (
            <Label htmlFor={inputId} className="custom-input-label">
              {label}
              {optional && (
                <span className="custom-input-optional"> (Optional)</span>
              )}
            </Label>
          )}
          <Input
            id={inputId}
            name={name}
            type={inputType}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className="custom-input-field"
            innerRef={innerRef || ref}
            {...rest}
          />
          {isPassword && value && (
            <button
              type="button"
              className="custom-input-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword
                ? hidePasswordIcon || <img src={eyeIcon} alt="Hide password" />
                : showPasswordIcon || (
                    <img src={eyeSlashIcon} alt="Show password" />
                  )}
            </button>
          )}
        </div>
        {invalid && errorText && (
          <div className="custom-input-feedback show">{errorText}</div>
        )}
      </div>
    );
  },
);

CustomInput.displayName = "CustomInput";

CustomInput.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  invalid: PropTypes.bool,
  errorText: PropTypes.string,
  optional: PropTypes.bool,
  className: PropTypes.string,
  showPasswordIcon: PropTypes.node,
  hidePasswordIcon: PropTypes.node,
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

export default CustomInput;
